import React, { useState, useEffect, useRef } from 'react';
import { getEffectiveLayout } from '../utils/multiWindowLive';
import useMultiWindowDevices from '../hooks/useMultiWindowDevices';
import useMultiWindowStreams from '../hooks/useMultiWindowStreams';
import useMultiWindowVisibility from '../hooks/useMultiWindowVisibility';
import useMultiWindowResourceUsage from '../hooks/useMultiWindowResourceUsage';
import PlayerGrid from '../components/multiWindowLive/PlayerGrid';
import MultiWindowHeader from '../components/multiWindowLive/MultiWindowHeader';
import MultiWindowSettingsModal from '../components/multiWindowLive/MultiWindowSettingsModal';
import './MultiWindowLive.css';

/**
 * 多窗口直播回传页面（薄壳）
 * 逻辑拆至 hooks + components/multiWindowLive，行为与拆分前一致。
 */
const MultiWindowLive = () => {
  const [currentLayout, setCurrentLayout] = useState(() => (
    typeof window !== 'undefined' && window.innerWidth <= 768 ? 'stack3' : 'quad'
  ));
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [showSettings, setShowSettings] = useState(false);
  const [fullscreenPlayer, setFullscreenPlayer] = useState(null);
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false)
  );

  // 跨 hook 桥接（可见性 5 分钟清空 ↔ 流 CRUD）
  const streamsRef = useRef([]);
  const clearAllStreamsSilentlyRef = useRef(() => {});

  const effectiveLayout = getEffectiveLayout(currentLayout, isMobileLayout);

  useEffect(() => {
    const onResize = () => setIsMobileLayout(window.innerWidth <= 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const devices = useMultiWindowDevices({
    autoRefresh,
    refreshInterval
  });

  const visibility = useMultiWindowVisibility({
    streamsRef,
    clearAllStreamsSilently: (...args) => clearAllStreamsSilentlyRef.current(...args)
  });

  const streams = useMultiWindowStreams({
    effectiveLayout,
    currentLayout,
    isMobileLayout,
    availableStreams: devices.availableStreams,
    streamsRef,
    setStreamStates: visibility.setStreamStates,
    memberMetaByKey: devices.memberMetaByKey
  });

  const { resourceUsage } = useMultiWindowResourceUsage();

  // 渲染期桥接：保证 visibility 空依赖回调始终打到最新 silently clear
  clearAllStreamsSilentlyRef.current = streams.clearAllStreamsSilently;
    
    return (
    <div className={`multi-window-live${isMobileLayout ? ' multi-window-live--mobile' : ''}`}>
      <MultiWindowHeader
        currentLayout={currentLayout}
        setCurrentLayout={setCurrentLayout}
        availableStreams={devices.availableStreams}
        streams={streams.streams}
        resourceUsage={resourceUsage}
        isPageVisible={visibility.isPageVisible}
        pageHiddenTime={visibility.pageHiddenTime}
        autoFillStreams={streams.autoFillStreams}
        clearAllStreams={streams.clearAllStreams}
        onOpenSettings={() => setShowSettings(true)}
      />

      <div className={`page-content${isMobileLayout && effectiveLayout.fitScreen ? ' page-content--fit' : ''}`}>
        {devices.isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <div>正在加载流媒体信息...</div>
          </div>
        ) : (
          <PlayerGrid
            effectiveLayout={effectiveLayout}
            isMobileLayout={isMobileLayout}
            streams={streams.streams}
            availableStreams={devices.availableStreams}
            memberMetaByKey={devices.memberMetaByKey}
            streamStates={visibility.streamStates}
            dragFromIndex={streams.dragFromIndex}
            setDragFromIndex={streams.setDragFromIndex}
            dragOverIndex={streams.dragOverIndex}
            setDragOverIndex={streams.setDragOverIndex}
            fullscreenPlayer={fullscreenPlayer}
            setFullscreenPlayer={setFullscreenPlayer}
            addStream={streams.addStream}
            removeStream={streams.removeStream}
            updateStreamProtocol={streams.updateStreamProtocol}
            moveStreamBetweenSlots={streams.moveStreamBetweenSlots}
          />
        )}
      </div>

      <MultiWindowSettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        autoRefresh={autoRefresh}
        setAutoRefresh={setAutoRefresh}
        refreshInterval={refreshInterval}
        setRefreshInterval={setRefreshInterval}
      />
    </div>
  );
};

export default MultiWindowLive; 
